// Copyright (c) 2026 Watch Tower Bible and Tract Society of Pennsylvania
// All Rights Reserved. Proprietary and confidential.
// Unauthorized copying, modification, or distribution is strictly prohibited.
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { FolderOpen, Lock, ArrowLeft, CheckCircle, XCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) {
      setError('Token inválido. Solicite um novo link de recuperação.');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }
    
    if (password.length < 8) {
      toast.error('A senha deve ter pelo menos 8 caracteres');
      return;
    }
    
    setIsLoading(true);
    
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success('Senha alterada com sucesso!');
    } catch (err) {
      const message = err.response?.data?.detail || 'Erro ao redefinir senha. O link pode ter expirado.';
      setError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const invalidLink = !!error && !token;
  let headerTitle = 'Nova Senha';
  let headerDescription = 'Digite sua nova senha';
  if (success) {
    headerTitle = 'Senha Alterada!';
    headerDescription = 'Sua senha foi alterada com sucesso';
  } else if (invalidLink) {
    headerTitle = 'Link Inválido';
    headerDescription = 'Solicite um novo link de recuperação';
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-md space-y-8">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3">
          <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center">
            <FolderOpen className="w-5 h-5 text-white" />
          </div>
          <span className="font-bold text-2xl tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Auditoria Trimestral
          </span>
        </div>

        <Card className="shadow-lg">
          <CardHeader className="text-center">
            <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              {headerTitle}
            </CardTitle>
            <CardDescription>
              {headerDescription}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {success ? (
              <div className="text-center space-y-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <p className="text-slate-600">
                  Você já pode fazer login com sua nova senha.
                </p>
                <Button
                  onClick={() => navigate('/')}
                  className="w-full bg-slate-900 hover:bg-slate-800"
                  data-testid="go-to-login-button"
                >
                  Ir para o Login
                </Button>
              </div>
            ) : error && !token ? (
              <div className="text-center space-y-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                  <XCircle className="w-8 h-8 text-red-600" />
                </div>
                <p className="text-slate-600">{error}</p>
                <Button
                  onClick={() => navigate('/forgot-password')}
                  className="w-full bg-slate-900 hover:bg-slate-800"
                >
                  Solicitar Novo Link
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-slate-700">Nova Senha</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="password"
                      data-testid="reset-password-input"
                      type="password"
                      placeholder="********"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10"
                      required
                      minLength={8}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-slate-700">Confirmar Senha</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="confirmPassword"
                      data-testid="reset-confirm-password-input"
                      type="password"
                      placeholder="********"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-10"
                      required
                      minLength={8}
                    />
                  </div>
                </div>

                {error && token && (
                  <p className="text-sm text-red-600">{error}</p>
                )}

                <Button 
                  type="submit" 
                  data-testid="reset-submit-button"
                  className="w-full bg-slate-900 hover:bg-slate-800"
                  disabled={isLoading}
                >
                  {isLoading ? 'Salvando...' : 'Salvar Nova Senha'}
                </Button>
              </form>
            )}

            <div className="mt-6 text-center">
              <Link 
                to="/" 
                className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Voltar para o login
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
